import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatTodayLabel() {
  return new Date().toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function EmergencyCallModal({
  open,
  onClose,
  defaultWorkerId,
  worksheetWorkers,
  onSent,
}) {
  const [workerId, setWorkerId] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [standbysToday, setStandbysToday] = useState([]);

  const worksheetWorkerMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(worksheetWorkers) ? worksheetWorkers : []).forEach((w) => {
      if (w?.workerId) map.set(String(w.workerId), w);
    });
    return map;
  }, [worksheetWorkers]);

  useEffect(() => {
    if (!open) return;
    setMessage("");
    setLoading(true);
    const token = localStorage.getItem("token");
    fetch(getBaseUrl() + "/api/on-call-standby/today", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const list = data.success && Array.isArray(data.data) ? data.data : [];
        setStandbysToday(list);
        const eligibleIds = new Set();
        for (const s of list) {
          const wid = String(s.workerId?._id || s.workerId || "");
          if (wid && worksheetWorkerMap.has(wid)) eligibleIds.add(wid);
        }
        const preferred = defaultWorkerId ? String(defaultWorkerId) : "";
        setWorkerId(preferred && eligibleIds.has(preferred) ? preferred : "");
      })
      .catch(() => {
        setStandbysToday([]);
        setWorkerId("");
        toast.error("Failed to load standby workers for today");
      })
      .finally(() => setLoading(false));
  }, [open, defaultWorkerId, worksheetWorkerMap]);

  const standbyWorkers = useMemo(() => {
    const list = [];
    for (const s of standbysToday) {
      const wid = String(s.workerId?._id || s.workerId || "");
      const ws = worksheetWorkerMap.get(wid);
      if (!wid || !ws) continue;
      list.push({
        workerId: wid,
        siteId: ws.siteId || String(s.siteId?._id || s.siteId || ""),
        siteName: ws.siteName || s.siteId?.siteName || "",
        workSheetId: ws.workSheetId || "",
        name: ws.name
          || (s.workerId
            ? `${s.workerId.firstName || ""} ${s.workerId.lastName || ""}`.trim() || s.workerId.email || "Unnamed"
            : "Unnamed"),
      });
    }
    const unique = new Map();
    list.forEach((w) => {
      if (!unique.has(w.workerId)) unique.set(w.workerId, w);
    });
    return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [standbysToday, worksheetWorkerMap]);

  const selectedWorker = standbyWorkers.find((w) => w.workerId === workerId) || null;
  const hasWorksheetWorkersToday = worksheetWorkerMap.size > 0;

  if (!open) return null;

  async function handleSend() {
    if (!workerId) {
      toast.error("Please select a worker on standby for today");
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/on-call-emergency", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          workerId,
          siteId: selectedWorker?.siteId || undefined,
          workSheetId: selectedWorker?.workSheetId || undefined,
          message: message.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || "Failed to send emergency call");
        return;
      }
      toast.success(data.message || "Emergency call sent");
      if (onSent) onSent(data.data);
      onClose();
    } catch {
      toast.error("Failed to send emergency call");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "480px",
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0" }}>
          <h5 style={{ margin: 0, color: "#8b1634", fontWeight: 700, fontSize: "18px" }}>Emergency On-Call</h5>
          <p style={{ margin: "8px 0 0", color: "#718096", fontSize: "14px" }}>
            Only workers with an <strong>active work sheet for today</strong> ({formatTodayLabel()}) who are also on{" "}
            <strong>standby today</strong> can receive an emergency call.
          </p>
        </div>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, color: "#4a5568", fontSize: "14px" }}>
              Worker (standby today)
            </label>
            {loading ? (
              <div style={{ fontSize: "14px", color: "#718096" }}>Loading standby workers...</div>
            ) : !hasWorksheetWorkersToday ? (
              <div
                style={{
                  fontSize: "14px",
                  color: "#b45309",
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "8px",
                  padding: "12px",
                }}
              >
                No workers have an active work sheet for today.
              </div>
            ) : standbyWorkers.length === 0 ? (
              <div
                style={{
                  fontSize: "14px",
                  color: "#b45309",
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "8px",
                  padding: "12px",
                }}
              >
                No workers on standby for today with an active work sheet. Add standby from the Standby page first.
              </div>
            ) : (
              <select
                value={workerId}
                onChange={(e) => setWorkerId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                }}
              >
                <option value="">Select worker</option>
                {standbyWorkers.map((w) => (
                  <option key={w.workerId} value={w.workerId}>
                    {w.name}
                    {w.siteName ? ` — ${w.siteName}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, color: "#4a5568", fontSize: "14px" }}>
              Message (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Emergency details for the worker..."
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                fontSize: "14px",
                resize: "vertical",
              }}
            />
          </div>
        </div>
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            background: "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || loading || standbyWorkers.length === 0 || !workerId}
            style={{
              padding: "8px 18px",
              borderRadius: "8px",
              border: "none",
              background: "#dc2626",
              color: "#fff",
              fontWeight: 600,
              cursor: sending || loading || standbyWorkers.length === 0 || !workerId ? "not-allowed" : "pointer",
              opacity: sending || loading || standbyWorkers.length === 0 || !workerId ? 0.7 : 1,
            }}
          >
            {sending ? "Sending..." : "Send Emergency Call"}
          </button>
        </div>
      </div>
    </div>
  );
}
